import React, { Fragment, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import { Tab } from '@mui/material';
import { TabPanel } from '@mui/lab';
import Chip from '@mui/material/Chip';
import ClearIcon from '@mui/icons-material/Clear';
import { Alert, FormGroup, FormText, Label } from 'reactstrap';
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import NestedMenu from './NestedMenu';
import {
  buildDisplayExecutionEnvironment,
  buildKey,
  isMacOs,
  isMobile,
  buildDeviceKey,
  updateEnvironmentWithNewOsItem,
  removeTreeNodeAndUpdate,
  clearOsVersionFromStorage,
  checkExistOsVersionInStorage,
  updateEnvironmentWithNewCustomDeviceName,
  clearDeviceNameFromStorage,
  checkExistDeviceNameInStorage,
  detectRunType,
} from '../smarttestscheduling/services/testcloud';

import { t } from '../../i18n/t';
import Select from '../Select';
import {
  ALL_BROWSERS_TESTCLOUD_AGENT,
  BROWSER_HEADLESS_SUFFIX,
  BrowserType,
  CloudType,
  ConfigType, OsType,
  RUN_TYPE
} from '../../utils/Constants';
import Routes from '../../utils/Routes';
import Link from '../Link';
import ToggleTunnelContainer from '../smarttestscheduling/components/ToggleTunnelContainer';
import MContext from '../../models/MContext';
import InfoIcon from '../../../images/icons/katalonui/InfoIcon';
import RegexInputDialog from './RegexInputDialog';
import { ConfirmDeleteDialog } from './ConfirmDeleteDialog';
import DocumentLink from '../../utils/DocumentLink';

export default function TestCloudEnvironment({
  testCloudEnv,
  updateTestCloudEnv,
  enabledTestCloudTunnel,
  toggleTestCloudTunnelIntegration,
  enableApiTesting, updateEnableApiTesting,
  tunnelId, handleSelectedTunnelChange,
  apps, handleChangeExecutionEnvironments, agents, configType, requireTestEnv,
  hideProfileWithTunnel = false, hideTabPanel = false,
  testSuiteId = null,
  initialTabValue = null,
}) {

  const initSelectedMap = new Map([
    [RUN_TYPE.MOBILE_BROWSERS, []],
    [RUN_TYPE.MOBILE_NATIVE_APP, []],
    [RUN_TYPE.DESKTOP_BROWSERS, []],
    [RUN_TYPE.WEB_SERVICES, []]
  ]);

  const initDisabledMap = new Map([
    [RUN_TYPE.MOBILE_BROWSERS, []],
    [RUN_TYPE.MOBILE_NATIVE_APP, []],
    [RUN_TYPE.DESKTOP_BROWSERS, []],
    [RUN_TYPE.WEB_SERVICES, []]
  ]);

  const getInitialTabValue = () => {
    // Use initialTabValue prop if provided
    if (initialTabValue) {
      return initialTabValue;
    }
    // Otherwise use agents if available
    if (!isEmpty(agents) && agents[0]) {
      return detectRunType(agents[0]);
    }
    // Default to desktop browsers
    return RUN_TYPE.DESKTOP_BROWSERS;
  };

  const [tabValue, setTabValue] = useState(getInitialTabValue());
  const [selectedExecutionEnvironmentsMap, setSelectedExecutionEnvironmentsMap] = useState(initSelectedMap);
  const [disabledExecutionEnvironmentsMap, setDisabledExecutionEnvironmentsMap] = useState(initDisabledMap);
  const [app, setApp] = useState({});

  const [isCustomOsDialogOpen, setIsCustomOsDialogOpen] = useState(false);
  const [isDeviceNameCustomDialogOpen, setIsDeviceNameCustomDialogOpen] = useState(false);

  const [actionItem, setActionItem] = useState(null);

  // For removing node of environment
  const [isDeleteCustomOsDialogOpen, setIsDeleteCustomOsDialogOpen] = useState(false);
  const [isDeleteCustomDeviceNameDialogOpen, setIsDeleteCustomDeviceNameDialogOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);

  // Specific environments for mobile and desktop
  const [mobileEnvironments, setMobileEnvironments] = useState();
  const [desktopEnvironments, setDesktopEnvironments] = useState();

  // Update tabValue when initialTabValue changes
  useEffect(() => {
    if (initialTabValue) {
      setTabValue(initialTabValue);
    }
  }, [initialTabValue]);

  const routes = new Routes();

  const numOfMobileLevels = 4;
  const numOfDesktopLevels = 3;

  const getBrowserType = (value) => {
    switch (value) {
      case RUN_TYPE.MOBILE_BROWSERS:
      case RUN_TYPE.MOBILE_NATIVE_APP:
      case RUN_TYPE.DESKTOP_BROWSERS:
        return BrowserType.ALL;
      case RUN_TYPE.WEB_SERVICES:
        return BrowserType.WEB_SERVICE;
      default:
        return null;
    }
  };

  const getApp = (value) => {
    if (RUN_TYPE.MOBILE_NATIVE_APP === value) {
      return apps.find((a) => a.value === app);
    }
    return null;
  };

  const filteredExecutionEnvironments = (tabValue) => {
    switch (tabValue) {
      case RUN_TYPE.MOBILE_BROWSERS:
      case RUN_TYPE.MOBILE_NATIVE_APP:
        return testCloudEnv.data.filter((el) => isMobile(el.value));
      case RUN_TYPE.DESKTOP_BROWSERS:
        return testCloudEnv.data.filter((el) => !isMobile(el.value))
          .map((el) => {
            const { children, ...object } = el;
            const result = children.filter((value) => !value.headless || configType !== ConfigType.COMMAND);
            return { ...object, children: result };
          });
      case RUN_TYPE.WEB_SERVICES:
        return testCloudEnv.data.filter((el) => !isMobile(el.value) && !isMacOs(el.value))
          .map((el) => {
            const { children, ...object } = el;
            const result = children.filter((value) => !value.headless);
            return { ...object, children: result };
          });
      default:
        return [];
    }
  };

  const handleChangeTab = (e, newValue) => {
    setTabValue(newValue);
  };

  const getEnvironmentParams = (configType, selectedItems, testSuiteId) => (configType === ConfigType.TSC
    ? { selectedItems, testSuiteId }
    : selectedItems);

  const handleChangeApp = (option) => {
    setApp(option.value);

    if (isEmpty(selectedExecutionEnvironmentsMap.get(tabValue)) && configType === ConfigType.TSC) {
      handleChangeExecutionEnvironments({
        selectedItems: [],
        testSuiteId,
        selectedApp: {
          appId: option.value,
          appName: option.name,
          appVersion: option.appVersion,
          appVersionCode: option.appVersionCode,
          appFileName: option.appFileName,
        },
      });
      return;
    }

    const selectedItems = selectedExecutionEnvironmentsMap.get(tabValue).map((value) => ({
      ...value,
      appId: option.value,
      appName: option.name,
      appFileName: option.fileName,
      appVersion: option.appVersion,
      appVersionCode: option.appVersionCode,
    }));

    setSelectedExecutionEnvironmentsMap((prevMap) => {
      const newMap = new Map(prevMap);
      newMap.set(tabValue, selectedItems);
      return newMap;
    });
    handleChangeExecutionEnvironments(getEnvironmentParams(configType, selectedItems, testSuiteId));
  };

  const handleChangeSelectedEnvs = (tabValue, env) => {
    const app = getApp(tabValue);
    const selected = { ...env,
      browserType: getBrowserType(tabValue),
      appId: app?.value,
      appName: app?.name,
      appFileName: app?.fileName,
      appVersion: app?.appVersion,
      appVersionCode: app?.appVersionCode,
      cloudType: CloudType.TEST_CLOUD_AGENT,
      runType: tabValue,
      enabledTestCloudTunnel };
    const selectedItems = [...selectedExecutionEnvironmentsMap.get(tabValue), selected];
    selectedExecutionEnvironmentsMap.set(tabValue, selectedItems);
    setSelectedExecutionEnvironmentsMap(selectedExecutionEnvironmentsMap);
  };

  const handleSelectWebServices = () => {
    const selected = {
      cloudType: CloudType.TEST_CLOUD_AGENT,
      enabledTestCloudTunnel,
      runType: RUN_TYPE.WEB_SERVICES,
      os: OsType.Linux.toLowerCase(),
      browser: ALL_BROWSERS_TESTCLOUD_AGENT.browser,
      browserType: BrowserType.WEB_SERVICE,
      browserVersion: ALL_BROWSERS_TESTCLOUD_AGENT.browserVersion,
    };

    const selectedItems = [selected];
    setSelectedExecutionEnvironmentsMap((prevMap) => {
      const newMap = new Map(prevMap);
      newMap.set(RUN_TYPE.WEB_SERVICES, selectedItems);
      return newMap;
    });
    handleChangeExecutionEnvironments(getEnvironmentParams(configType, selectedItems, testSuiteId));
  };

  const handleChangeDisabledEnvs = (tabValue, env) => {
    const disableItems = [...disabledExecutionEnvironmentsMap.get(tabValue), env.key];
    disabledExecutionEnvironmentsMap.set(tabValue, disableItems);
    setDisabledExecutionEnvironmentsMap(disabledExecutionEnvironmentsMap);
  };

  const handleDeleteSelectedEnvs = (tabValue, env) => {
    setSelectedExecutionEnvironmentsMap((prevMap) => {
      const newMap = new Map(prevMap);
      const newSelectedItems = newMap.get(tabValue).filter((item) => item.key !== env.key);
      newMap.set(tabValue, newSelectedItems);
      const environmentParams = configType === ConfigType.TSC
        ? { selectedItems: [], testSuiteId }
        : newMap.get(tabValue);

      handleChangeExecutionEnvironments(environmentParams);
      return newMap;
    });
  };

  const handleDeleteDisabledEnvs = (tabValue, env) => {
    setDisabledExecutionEnvironmentsMap((prevMap) => {
      const newDisabledMap = new Map(prevMap);
      const environmentParams = configType === ConfigType.TSC ?
        [] : newDisabledMap.get(tabValue).filter((item) => item !== env.key);
      newDisabledMap.set(tabValue, environmentParams);
      return newDisabledMap;
    });
  };

  const handleSelectedItem = (env) => {
    handleChangeSelectedEnvs(tabValue, env);
    handleChangeDisabledEnvs(tabValue, env);
    handleChangeExecutionEnvironments(getEnvironmentParams(configType, selectedExecutionEnvironmentsMap.get(tabValue), testSuiteId));
  };

  const handleDeleteItem = (env) => {
    handleDeleteSelectedEnvs(tabValue, env);
    handleDeleteDisabledEnvs(tabValue, env);
  };

  const transformAgens = (agents) => {
    if (isMobile(agents[0].os)) {
      return agents.map((agent) => ({ ...agent, key: buildDeviceKey(agent.deviceId, agent.os, agent.osVersion, agent.devicePoolId, agent.deviceUniqueName) }));
    }
    return agents.map((agent) => ({ ...agent,
      key: buildKey(agent.os, agent.headless && !agent.browser?.includes(BROWSER_HEADLESS_SUFFIX) ?
        `${agent.browser}${BROWSER_HEADLESS_SUFFIX}` : agent.browser, agent.browserVersion) }));
  };

  const dialogHandlers = {
    custom_os_version: (value) => {
      setIsCustomOsDialogOpen(true);
      setActionItem(value);
    },
    custom_device_name: (value) => {
      setIsDeviceNameCustomDialogOpen(true);
      setActionItem(value);
    },
  };

  const handleActionClick = (value) => {
    const handler = dialogHandlers[value.value];
    if (handler) {
      handler(value);
    }
  };

  const handleDeleteClick = (node) => {
    setDeleteItem(node);
    if (node.children && node.children.length > 0) {
      setIsDeleteCustomOsDialogOpen(true);
      return;
    }
    setIsDeleteCustomDeviceNameDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (deleteItem) {
      removeTreeNodeAndUpdate(deleteItem, updateTestCloudEnv);
      clearOsVersionFromStorage(deleteItem?.extraInfo?.os, deleteItem?.extraInfo?.osVersion);
    }
    setIsDeleteCustomOsDialogOpen(false);
    setDeleteItem(null);
  };

  const handleConfirmDeleteCustomDeviceName = () => {
    if (deleteItem) {
      const { os, osVersion, isTablet, deviceName } = deleteItem.extraInfo || {};
      removeTreeNodeAndUpdate(deleteItem, updateTestCloudEnv);
      clearDeviceNameFromStorage(os, osVersion, isTablet, deviceName);
    }
    setIsDeleteCustomDeviceNameDialogOpen(false);
    setDeleteItem(null);
  };

  const handleCloseDeleteDialog = () => {
    setIsDeleteCustomOsDialogOpen(false);
    setIsDeleteCustomDeviceNameDialogOpen(false);
    setDeleteItem(null);
  };

  useEffect(() => {
    if (isEmpty(agents) || !agents[0]) {
      return;
    }

    const result = transformAgens(agents);
    const keys = result.map((agent) => agent.key);
    setTabValue(() => {
      const tabValue = detectRunType(agents[0]);
      selectedExecutionEnvironmentsMap.set(tabValue, result);
      disabledExecutionEnvironmentsMap.set(tabValue, keys);
      return tabValue;
    });

  }, [agents]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!isEmpty(agents) && agents[0]?.appId) {
      setApp(agents[0].appId);
    } else if (!isEmpty(apps)) {
      setApp(apps[0].value);
    }
  }, [agents, apps]);

  const updateSpecificEnvironments = (tabValue, filteredEnvs) => {
    if ([RUN_TYPE.MOBILE_BROWSERS, RUN_TYPE.MOBILE_NATIVE_APP].includes(tabValue)) {
      setMobileEnvironments(filteredEnvs);
    } else if (tabValue === RUN_TYPE.DESKTOP_BROWSERS) {
      setDesktopEnvironments(filteredEnvs);
    }
  };

  useEffect(() => {
    updateSpecificEnvironments(tabValue, filteredExecutionEnvironments(tabValue));
    const environmentParams = getEnvironmentParams(configType, selectedExecutionEnvironmentsMap.get(tabValue), testSuiteId);
    handleChangeExecutionEnvironments(environmentParams);
  }, [tabValue, testCloudEnv.data]); // eslint-disable-line react-hooks/exhaustive-deps

  const isSingleSelectionMode = (configType, selectedExecutionEnvironmentsMap, tabValue) => configType === ConfigType.TSC && selectedExecutionEnvironmentsMap.get(tabValue).length >= 1;

  const handleSaveRegex = (value) => {
    updateEnvironmentWithNewOsItem(actionItem?.extraInfo?.os, value, updateTestCloudEnv);
    setIsCustomOsDialogOpen(false);
    setActionItem(null);
  };

  const handleSaveCustomDeviceName = (value) => {
    updateEnvironmentWithNewCustomDeviceName(actionItem?.extraInfo, value, updateTestCloudEnv);
    setIsDeviceNameCustomDialogOpen(false);
    setActionItem(null);
  };

  const renderHelperComponent = (description, examples) => (
    <FormText color="muted">
      {description} Eg. {examples.map((example) => (
        <Fragment key={example}>
          {examples.indexOf(example) > 0 && ', '}
          <code>{example}</code>
        </Fragment>
      ))}. Learn how to use RegEx&nbsp;
      <Link href={DocumentLink.TEST_CLOUD_SET_REGEX_CAPABILITIES} target="_blank" rel="noopener noreferrer">
        here.
      </Link>
    </FormText>
  );

  // Usage for custom OS version
  const renderOsVersionHelperComponent = () =>
    renderHelperComponent(
      'Please enter your desired OS version, following the Regular Expression format.',
      ['16.*', '(16.*)', '(1(3|4).*)']
    );

  // Usage for custom Device name
  const renderCustomDeviceNameHelperComponent = () =>
    renderHelperComponent(
      'Please enter your custom device name, following the Regular Expression format.',
      ['iPhone.*', '(Pixel.*)', '(OnePlus [^8].*)']
    );

  const checkDuplicateOsVersion = (osVersion) => {
    const os = actionItem?.extraInfo?.os;
    if (checkExistOsVersionInStorage(os, osVersion)) {
      return t('schedule#duplicate-config-error');
    }
    return '';
  };

  const checkDuplicateCustomDeviceName = (deviceName) => {
    const { os, osVersion, isTablet } = actionItem?.extraInfo || {};
    if (checkExistDeviceNameInStorage(os, osVersion, isTablet, deviceName)) {
      return t('schedule#duplicate-config-error');
    }
    return '';
  };

  return (
    <Box>
      <RegexInputDialog
        isOpen={isCustomOsDialogOpen}
        onClose={() => setIsCustomOsDialogOpen(false)}
        onSave={handleSaveRegex}
        helperComponent={renderOsVersionHelperComponent()}
        customCheck={checkDuplicateOsVersion}
        title={t('schedule#custom-os-version')}
      />
      <RegexInputDialog
        isOpen={isDeviceNameCustomDialogOpen}
        onClose={() => setIsDeviceNameCustomDialogOpen(false)}
        onSave={handleSaveCustomDeviceName}
        helperComponent={renderCustomDeviceNameHelperComponent()}
        customCheck={checkDuplicateCustomDeviceName}
        title={t('schedule#custom-device-name')}
      />
      <ConfirmDeleteDialog
        isOpen={isDeleteCustomOsDialogOpen}
        handleClose={handleCloseDeleteDialog}
        handleConfirm={handleConfirmDelete}
        message={t('schedule#delete-os-confirm', { osName: deleteItem?.value })}
        title={t('schedule#delete-custom-os')}
        confirmButtonText={t('delete')}
        cancelButtonText={t('cancel')}
      />
      <ConfirmDeleteDialog
        isOpen={isDeleteCustomDeviceNameDialogOpen}
        handleClose={handleCloseDeleteDialog}
        handleConfirm={handleConfirmDeleteCustomDeviceName}
        message={t('schedule#delete-custom-device-name-confirm', { deviceName: deleteItem?.value })}
        title={t('schedule#delete-custom-device-name')}
        confirmButtonText={t('delete')}
        cancelButtonText={t('cancel')}
      />
      <Box sx={{
        padding: '0.25rem 0.75rem',
        borderRadius: '6px',
        border: 'solid 1px #d5d8dd',
        backgroundColor: '#fff',
        minHeight: '2.5rem',
        maxHeight: '5rem',
        margin: '0.5rem 0 0.5rem 0',
        overflowY: 'scroll'
      }}
      >{
          selectedExecutionEnvironmentsMap.get(tabValue).map((item) => (
            <Chip
              sx={{ borderRadius: '5px', height: '1.5rem', margin: '0.25rem 0.5rem 0.25rem 0' }}
              deleteIcon={tabValue === RUN_TYPE.WEB_SERVICES ? undefined : <ClearIcon />}
              key={item.key}
              label={buildDisplayExecutionEnvironment(item)}
              onDelete={tabValue === RUN_TYPE.WEB_SERVICES ? null : () => handleDeleteItem(item)}
            />))
        }
      </Box>
      <TabContext value={tabValue}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <TabList className="tab-active" onChange={handleChangeTab} aria-label="lab API tabs example">
            <Tab label="Desktop Browsers" value={RUN_TYPE.DESKTOP_BROWSERS} />
            {!hideTabPanel && <Tab label="Mobile Browsers" value={RUN_TYPE.MOBILE_BROWSERS} />}
            {MContext.testCloudMobileNativeAppEnabled && !hideTabPanel && <Tab label="Mobile Native App" value={RUN_TYPE.MOBILE_NATIVE_APP} />}
            {!hideTabPanel && <Tab
              label="Web Services"
              onClick={handleSelectWebServices}
              value={RUN_TYPE.WEB_SERVICES}
            />}
          </TabList>
        </Box>
        {!hideTabPanel &&
          <TabPanel sx={{ padding: '1.5rem 0' }} value={RUN_TYPE.MOBILE_BROWSERS}>
            <NestedMenu
              options={mobileEnvironments}
              numOfLevels={numOfMobileLevels}
              handleSelectedItem={handleSelectedItem}
              disabledItems={disabledExecutionEnvironmentsMap.get(tabValue)}
              singleSelectionMode={isSingleSelectionMode(configType, selectedExecutionEnvironmentsMap, tabValue)}
              onAction={handleActionClick}
              onDelete={handleDeleteClick}
            />
            {!hideProfileWithTunnel && configType !== ConfigType.TSC &&
            <ToggleTunnelContainer
              enabledTestCloudTunnel={enabledTestCloudTunnel}
              toggleTestCloudTunnelIntegration={toggleTestCloudTunnelIntegration}
              enableApiTesting={enableApiTesting}
              updateEnableApiTesting={updateEnableApiTesting}
              tunnelId={tunnelId}
              handleSelectedTunnelChange={handleSelectedTunnelChange}
            />}
          </TabPanel>}
        {!hideTabPanel && MContext.testCloudMobileNativeAppEnabled &&
          <TabPanel sx={{ padding: '1.5rem 0' }} value={RUN_TYPE.MOBILE_NATIVE_APP}>
            <NestedMenu
              options={mobileEnvironments}
              numOfLevels={numOfMobileLevels}
              handleSelectedItem={handleSelectedItem}
              disabledItems={disabledExecutionEnvironmentsMap.get(tabValue)}
              singleSelectionMode={isSingleSelectionMode(configType, selectedExecutionEnvironmentsMap, tabValue)}
              onAction={handleActionClick}
              onDelete={handleDeleteClick}
            />
            {configType !== ConfigType.TSC &&
            <ToggleTunnelContainer
              enabledTestCloudTunnel={enabledTestCloudTunnel}
              toggleTestCloudTunnelIntegration={toggleTestCloudTunnelIntegration}
              enableApiTesting={enableApiTesting}
              updateEnableApiTesting={updateEnableApiTesting}
              tunnelId={tunnelId}
              handleSelectedTunnelChange={handleSelectedTunnelChange}
            />}
            <FormGroup>
              <div className="d-flex">
                <Label for="mobileNative" className="normal-label">{t('Mobile Native App')}</Label>
              </div>
              <Select
                required
                clearable={false}
                id="mobileNative"
                value={app}
                onChange={handleChangeApp}
                options={apps}
                maxMenuHeight={125}
                menuPosition="fixed"
                isCustom
                styles={{
                  menu: (provided) => ({
                    ...provided,
                    position: 'relative'
                  })
                }}
              />
              <FormText>
                {t('schedule-v1#select-native-app#desc')}
                <Link href={routes.native_apps_link}>
                  {t('schedule-v1#select-native-app#click-here-to-upload')}
                </Link>.
              </FormText>
              {requireTestEnv && <small className="text-danger fs-6">This is required</small>}
            </FormGroup>
          </TabPanel>}
        <TabPanel sx={{ padding: '1.5rem 0' }} value={RUN_TYPE.DESKTOP_BROWSERS}>
          <NestedMenu
            options={desktopEnvironments}
            numOfLevels={numOfDesktopLevels}
            handleSelectedItem={handleSelectedItem}
            disabledItems={disabledExecutionEnvironmentsMap.get(tabValue)}
            singleSelectionMode={isSingleSelectionMode(configType, selectedExecutionEnvironmentsMap, tabValue)}
          />
          {!hideProfileWithTunnel && configType !== ConfigType.TSC &&
            <ToggleTunnelContainer
              enabledTestCloudTunnel={enabledTestCloudTunnel}
              toggleTestCloudTunnelIntegration={toggleTestCloudTunnelIntegration}
              enableApiTesting={enableApiTesting}
              updateEnableApiTesting={updateEnableApiTesting}
              tunnelId={tunnelId}
              handleSelectedTunnelChange={handleSelectedTunnelChange}
            />}
        </TabPanel>
        {!hideTabPanel &&
          <TabPanel
            sx={{ padding: '1.5rem 0' }}
            value={RUN_TYPE.WEB_SERVICES}
          >
            <Alert color="primary"><InfoIcon /> {
                  t('run-configuration#test-suite-web-services-guide')
                }
            </Alert>
            {!hideProfileWithTunnel && configType !== ConfigType.TSC &&
            <ToggleTunnelContainer
              enabledTestCloudTunnel={enabledTestCloudTunnel}
              toggleTestCloudTunnelIntegration={toggleTestCloudTunnelIntegration}
              tunnelId={tunnelId}
              handleSelectedTunnelChange={handleSelectedTunnelChange}
            />}
          </TabPanel>}
      </TabContext>
    </Box>
  );
}

TestCloudEnvironment.propTypes = {
  testCloudEnv: PropTypes.object,
  updateTestCloudEnv: PropTypes.func,
  enabledTestCloudTunnel: PropTypes.bool,
  toggleTestCloudTunnelIntegration: PropTypes.func,
  enableApiTesting: PropTypes.bool,
  updateEnableApiTesting: PropTypes.func,
  apps: PropTypes.array,
  handleChangeExecutionEnvironments: PropTypes.func,
  agents: PropTypes.array,
  configType: PropTypes.string,
  requireTestEnv: PropTypes.bool,
  hideProfileWithTunnel: PropTypes.bool,
  hideTabPanel: PropTypes.bool,
  tunnelId: PropTypes.string || null,
  handleSelectedTunnelChange: PropTypes.func,
  testSuiteId: PropTypes.number,
  initialTabValue: PropTypes.string,
};

TestCloudEnvironment.defaultProps = {
  testSuiteId: null,
};
